import { ArticleSseEvent, GenerationStage } from '../../types/articleGeneration';

/**
 * Feed raw SSE text chunks; returns typed events plus the unfinished trailing
 * text to pass back in as `buffer` with the next chunk.
 */
export function parseSseChunk(
  buffer: string,
  chunk: string
): { events: ArticleSseEvent[]; rest: string } {
  const text = buffer + chunk;
  const lines = text.split('\n');
  const rest = lines.pop() ?? '';
  const events: ArticleSseEvent[] = [];

  for (const line of lines) {
    if (!line.startsWith('data: ')) continue;
    let data: Record<string, unknown>;
    try {
      data = JSON.parse(line.slice(6));
    } catch {
      continue;
    }
    if (typeof data.content === 'string') {
      events.push({ kind: 'content', text: data.content });
    } else if (data.type === 'generation') {
      events.push({
        kind: 'generation',
        id: String(data.id),
        writerModel: data.writerModel ? String(data.writerModel) : undefined,
      });
    } else if (data.type === 'critic_progress') {
      events.push({
        kind: 'critic_progress',
        target: data.target as 'skeleton' | 'article',
        round: Number(data.round),
        slot: Number(data.slot),
        label: String(data.label),
        model: String(data.model),
        state: data.state as 'sent' | 'received',
        verdict: data.verdict as 'positive' | 'negative' | undefined,
        ok: data.ok as boolean | undefined,
      });
    } else if (data.type === 'status') {
      events.push({
        kind: 'status',
        stage: data.stage as GenerationStage,
        section: data.section as number | undefined,
        totalSections: data.totalSections as number | undefined,
        heading: data.heading as string | undefined,
      });
    } else if (data.type === 'retry') {
      events.push({
        kind: 'retry',
        stage: String(data.stage),
        attempt: Number(data.attempt),
        maxAttempts: Number(data.maxAttempts),
      });
    } else if (data.type === 'critique') {
      events.push({
        kind: 'critique',
        target: data.target as 'skeleton' | 'article',
        round: Number(data.round),
        positive: Number(data.positive),
        total: Number(data.total),
        reason: data.reason ? String(data.reason) : undefined,
      });
    } else if (data.type === 'result') {
      events.push({ kind: 'result', payload: data.payload as never });
    } else if (data.type === 'handoff') {
      events.push({
        kind: 'handoff',
        hop: Number(data.hop),
        after: data.after ? String(data.after) : undefined,
      });
    } else if (data.type === 'cancelled') {
      events.push({ kind: 'cancelled' });
    } else if (data.type === 'needs_review') {
      events.push({
        kind: 'needs_review',
        target: data.target as 'skeleton' | 'article',
        unaccepted: Array.isArray(data.unaccepted) ? (data.unaccepted as number[]).map(Number) : [],
      });
    } else if (data.type === 'done') {
      events.push({ kind: 'done' });
    } else if (data.type === 'error') {
      events.push({ kind: 'error', message: String(data.message ?? 'Unknown error') });
    }
  }
  return { events, rest };
}
